import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * Tine ultima valoare din `sizes` in acord cu numarul real de coloane de pe mobil.
 * Lista conteaza doar in browserele fara `sizes=auto`; acolo o coloana cere 100vw,
 * doua cer 50vw. Numarul vine din `--mobile-columns`, pe care tema il pune inline.
 *
 * @param {Element | null | undefined} grid
 */
function syncMobileSizes(grid) {
  if (!grid) return;
  const single = grid.getAttribute('product-grid-view') === 'mobile-single';
  const cols = single ? 1 : parseInt(getComputedStyle(grid).getPropertyValue('--mobile-columns'), 10) || 2;
  const vw = `, ${Math.round(100 / cols)}vw`;
  for (const img of grid.querySelectorAll('img[sizes]')) {
    const current = img.getAttribute('sizes');
    if (!current || !/,\s*\d+vw$/.test(current)) continue;
    const next = current.replace(/,\s*\d+vw$/, vw);
    if (next !== current) img.setAttribute('sizes', next);
  }
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    const { grid } = this.refs;
    if (grid) {
      syncMobileSizes(grid);
      this.#gridObserver = new MutationObserver(() => syncMobileSizes(grid));
      this.#gridObserver.observe(grid, { childList: true });
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver?.disconnect();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    syncMobileSizes(grid);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
